// Application entry. Wires together camera → detector → tracker → registry →
// alerts → renderer → storage. Hot-applies config changes from the sidebar.

import { Detector } from "./inference/detector";
import { ByteTracker } from "./inference/tracker";
import {
  COCO_CLASSES,
  DEFAULT_CONFIG,
  type AlertEvent,
  type AppConfig,
  type Detection,
} from "./inference/types";
import { AlertEngine } from "./runtime/alerts";
import { FrameLoop, listCameras, startCamera } from "./runtime/camera";
import { DangerZone } from "./runtime/danger_zone";
import { Overlay } from "./runtime/overlay";
import { TrackRegistry } from "./runtime/tracking_registry";
import { EventStore, MAX_EVENTS } from "./storage/indexeddb";
import { EventsTable } from "./ui/events_table";
import { PolygonEditor } from "./ui/polygon_editor";
import { Settings } from "./ui/settings";
import { sendEventWebhook, sendTestWebhook } from "./runtime/webhook";

interface RuntimeState {
  config: AppConfig;
  detector: Detector;
  tracker: ByteTracker;
  registry: TrackRegistry;
  dangerZone: DangerZone | null;
  alerts: AlertEngine | null;
  store: EventStore;
  overlay: Overlay;
  events: EventsTable;
  cameraStream: Awaited<ReturnType<typeof startCamera>> | null;
  loop: FrameLoop | null;
  alertCount: number;
  latestEvent: AlertEvent | null;
  latestDetectionInZone: Detection | null; // for live banner
  lastFrameTime: number;
  measuredFps: number;
  detectionCount: number;
  inferenceMs: number;
  videoEl: HTMLVideoElement;
  overlayCanvas: HTMLCanvasElement;
  polygonEditor: PolygonEditor;
  autoPaused: boolean; // backgrounded the tab
}

async function boot(): Promise<void> {
  const status = document.getElementById("status") as HTMLDivElement;
  status.textContent = "loading model…";

  const videoEl = document.getElementById("video") as HTMLVideoElement;
  const overlayCanvas = document.getElementById("overlay") as HTMLCanvasElement;

  const config: AppConfig = { ...DEFAULT_CONFIG, ...loadPersistedWebhook() };

  // Probe WebGPU availability up-front so we don't try to load with a backend
  // that doesn't exist (iOS Safari < 18 has no WebGPU; many older Androids too).
  const webgpuAvailable = !!(navigator as Navigator & { gpu?: unknown }).gpu;
  const deviceSelect = document.getElementById("device-select") as HTMLSelectElement;
  if (!webgpuAvailable) {
    const webgpuOpt = deviceSelect.querySelector<HTMLOptionElement>("option[value='webgpu']");
    if (webgpuOpt) {
      webgpuOpt.disabled = true;
      webgpuOpt.textContent = "WebGPU (not supported on this browser)";
    }
    if (config.inference_device === "webgpu") {
      config.inference_device = "wasm";
      deviceSelect.value = "wasm";
    }
  }

  const detector = new Detector({
    modelUrl: "/models/yolo11n.onnx",
    device: config.inference_device,
    confidenceThreshold: config.confidence_threshold,
    classes: config.classes,
  });
  await detector.load();
  const activeBackend = config.inference_device === "webgpu" ? "WebGPU" : "WASM";
  status.textContent = `model loaded (${activeBackend}) — click 'Start camera'`;

  const tracker = new ByteTracker({
    highThresh: config.confidence_threshold,
    lowThresh: 0.1,
    trackBuffer: 90,
    matchThresh: 0.8,
  });
  const registry = new TrackRegistry({ graceSeconds: config.track_grace_seconds });
  const overlay = new Overlay(overlayCanvas);
  const store = new EventStore(config.storage_mode);
  const events = new EventsTable(store);
  await events.refresh();

  const polygonEditor = new PolygonEditor({
    canvas: overlayCanvas,
    initial: config.danger_zone_polygon,
    onChange: (poly) => {
      state.config.danger_zone_polygon = poly;
      state.dangerZone = poly.length >= 3 ? new DangerZone(poly) : null;
      if (state.alerts && state.dangerZone) state.alerts.dangerZone = state.dangerZone;
      const clearBtn = document.getElementById("btn-zone-clear") as HTMLButtonElement;
      clearBtn.disabled = poly.length === 0;
    },
  });
  // Reflect the initial polygon state into the Clear-zone button at boot —
  // onChange only fires on edits, not on construction.
  (document.getElementById("btn-zone-clear") as HTMLButtonElement).disabled =
    config.danger_zone_polygon.length === 0;

  const dangerZone = config.danger_zone_polygon.length >= 3
    ? new DangerZone(config.danger_zone_polygon)
    : null;
  const alerts = dangerZone && config.alert_on_danger_zone
    ? new AlertEngine({
        dangerZone,
        minPersistenceSeconds: config.min_persistence_seconds,
        minConfidence: config.min_alert_confidence,
        cooldownSeconds: config.alert_cooldown_seconds,
      })
    : null;

  const state: RuntimeState = {
    config,
    detector,
    tracker,
    registry,
    dangerZone,
    alerts,
    store,
    overlay,
    events,
    cameraStream: null,
    loop: null,
    alertCount: 0,
    latestEvent: null,
    latestDetectionInZone: null,
    lastFrameTime: 0,
    measuredFps: 0,
    detectionCount: 0,
    inferenceMs: 0,
    videoEl,
    overlayCanvas,
    polygonEditor,
    autoPaused: false,
  };

  // Settings sidebar
  const settings = new Settings({
    onChange: (cfg, reason) => onConfigChange(state, cfg, reason),
    onClearZone: () => {
      state.polygonEditor.clear();
      state.dangerZone = null;
      state.alerts = null;
      const clearBtn = document.getElementById("btn-zone-clear") as HTMLButtonElement;
      clearBtn.disabled = true;
    },
    onExportZip: async () => {
      const blob = await state.store.exportZip();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `marine-detect-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onClearHistory: async () => {
      await state.store.clear();
      state.alertCount = 0;
      state.latestEvent = null;
      await state.events.refresh();
      await updateEventsCounter(state);
    },
    onTestWebhook: async () => {
      const statusEl = document.getElementById("webhook-status") as HTMLSpanElement;
      statusEl.textContent = "sending…";
      const r = await sendTestWebhook({
        enabled: true,
        url: state.config.webhook_url,
        apiKey: state.config.webhook_api_key,
      });
      if (r.ok) {
        statusEl.textContent = `ok (${r.status})`;
        statusEl.style.color = "var(--ok, #4ade80)";
      } else {
        statusEl.textContent = r.status
          ? `failed (${r.status})`
          : `failed: ${r.error ?? "network error"}`;
        statusEl.style.color = "var(--bad, #f87171)";
      }
    },
  });
  settings.setConfig(config);
  await updateEventsCounter(state);

  // Camera dropdown — browsers hide device labels until permission is granted,
  // so we show a single placeholder until after the first successful Start.
  const camSelect = document.getElementById("cam-select") as HTMLSelectElement;
  setCamPlaceholder(camSelect);

  // Start / pause / stop / fullscreen buttons
  const startBtn = document.getElementById("btn-start") as HTMLButtonElement;
  const pauseBtn = document.getElementById("btn-pause") as HTMLButtonElement;
  const stopBtn = document.getElementById("btn-stop") as HTMLButtonElement;
  const fullscreenBtn = document.getElementById("btn-fullscreen") as HTMLButtonElement;
  const fsExitBtn = document.getElementById("btn-fs-exit") as HTMLButtonElement;
  const fsPauseBtn = document.getElementById("btn-fs-pause") as HTMLButtonElement;
  const fsStopBtn = document.getElementById("btn-fs-stop") as HTMLButtonElement;

  const setIdle = () => {
    startBtn.disabled = false;
    pauseBtn.disabled = true;
    stopBtn.disabled = true;
    fullscreenBtn.disabled = true;
    pauseBtn.textContent = "Pause";
    fsPauseBtn.textContent = "Pause";
  };
  const setLive = () => {
    startBtn.disabled = true;
    pauseBtn.disabled = false;
    stopBtn.disabled = false;
    fullscreenBtn.disabled = false;
  };

  const setPaused = (paused: boolean) => {
    const text = paused ? "Resume" : "Pause";
    pauseBtn.textContent = text;
    fsPauseBtn.textContent = text;
    if (paused) status.textContent = "paused";
    else if (state.cameraStream) {
      status.textContent = `live — ${state.cameraStream.width}×${state.cameraStream.height}`;
    }
  };

  // Idempotent: tears down camera + loop + overlay state without touching
  // the model or registry. Safe to call repeatedly.
  const stopCamera = () => {
    state.loop?.stop();
    state.loop = null;
    state.cameraStream?.stop();
    state.cameraStream = null;
    state.tracker.reset();
    state.latestEvent = null;
    state.latestDetectionInZone = null;
    state.measuredFps = 0;
    state.lastFrameTime = 0;
    const ctx = overlayCanvas.getContext("2d");
    if (ctx) ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
  };

  // Awaits the full start flow so callers (Start button, camera-switch) can
  // sequence cleanly. Always tears down any prior stream + loop first so
  // back-to-back calls don't leak FrameLoops or stack inferences.
  const startCameraStream = async (deviceId: string | undefined): Promise<void> => {
    stopCamera();
    startBtn.disabled = true;
    status.textContent = "starting camera…";
    try {
      state.cameraStream = await startCamera(videoEl, { deviceId });
      await refreshCamList(camSelect);
      // Make sure the dropdown reflects the camera we actually got.
      const actualDeviceId = state.cameraStream.stream
        .getVideoTracks()[0]?.getSettings().deviceId;
      if (actualDeviceId) camSelect.value = actualDeviceId;
      sizeOverlayToVideo(state);
      state.polygonEditor.setFrameSize({
        w: state.cameraStream.width,
        h: state.cameraStream.height,
      });
      state.loop = new FrameLoop(state.config.capture_fps, () => processFrame(state));
      state.loop.start();
      setLive();
      status.textContent = `live — ${state.cameraStream.width}×${state.cameraStream.height}`;
    } catch (err) {
      console.error(err);
      status.textContent = `camera error: ${(err as Error).message}`;
      setIdle();
    }
  };

  startBtn.addEventListener("click", async () => {
    if (state.cameraStream) return;
    await startCameraStream(camSelect.value || undefined);
  });

  const togglePause = () => {
    if (!state.loop) return;
    if (pauseBtn.textContent === "Pause") {
      state.loop.stop();
      setPaused(true);
    } else {
      state.loop.start();
      setPaused(false);
    }
  };
  pauseBtn.addEventListener("click", togglePause);
  fsPauseBtn.addEventListener("click", togglePause);

  const handleStopClick = () => {
    stopCamera();
    setIdle();
    status.textContent = "stopped — model still loaded";
    if (document.body.classList.contains("fullscreen")) exitFullscreen();
  };
  stopBtn.addEventListener("click", handleStopClick);
  fsStopBtn.addEventListener("click", handleStopClick);

  const enterFullscreen = async () => {
    document.body.classList.add("fullscreen");
    sizeOverlayToVideo(state);
    if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
      } catch {
        // Some browsers (notably iOS Safari) reject without a user gesture or
        // disallow it on <html>. The CSS-only mode still works as a fallback.
      }
    }
  };
  const exitFullscreen = () => {
    document.body.classList.remove("fullscreen");
    if (document.fullscreenElement && document.exitFullscreen) {
      void document.exitFullscreen();
    }
  };
  fullscreenBtn.addEventListener("click", () => {
    if (document.body.classList.contains("fullscreen")) exitFullscreen();
    else void enterFullscreen();
  });
  fsExitBtn.addEventListener("click", exitFullscreen);

  // Keep CSS state in sync if the user presses ESC or the OS exits fullscreen.
  document.addEventListener("fullscreenchange", () => {
    if (!document.fullscreenElement && document.body.classList.contains("fullscreen")) {
      document.body.classList.remove("fullscreen");
    }
  });

  // When the user picks a different camera mid-session, restart with the
  // new device. Awaits the restart so the UI never enters a half-stopped
  // state where multiple FrameLoops could leak.
  let switchingCamera = false;
  camSelect.addEventListener("change", async () => {
    if (!state.cameraStream || switchingCamera) return;
    switchingCamera = true;
    try {
      await startCameraStream(camSelect.value || undefined);
    } finally {
      switchingCamera = false;
    }
  });

  // Re-sync overlay canvas dimensions when the underlying video changes —
  // mobile streams renegotiate on orientation flip and front/back swap.
  videoEl.addEventListener("resize", () => sizeOverlayToVideo(state));
  window.addEventListener("orientationchange", () => sizeOverlayToVideo(state));

  // Auto-pause when the tab is hidden; auto-resume when visible again
  // (only if the user wasn't manually paused).
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      if (state.loop && pauseBtn.textContent === "Pause") {
        state.loop.stop();
        state.autoPaused = true;
        status.textContent = "auto-paused (tab hidden)";
      }
    } else if (state.autoPaused && state.loop && state.cameraStream) {
      state.loop.start();
      state.autoPaused = false;
      status.textContent = `live — ${state.cameraStream.width}×${state.cameraStream.height}`;
    }
  });
}

function setCamPlaceholder(select: HTMLSelectElement): void {
  // Browsers won't reveal device labels until permission is granted, so listing
  // them here would just show "camera 1", "camera 2" with no useful info.
  select.innerHTML = "";
  const opt = document.createElement("option");
  opt.value = "";
  opt.textContent = "Default camera (allow access to choose)";
  select.appendChild(opt);
}

async function refreshCamList(select: HTMLSelectElement): Promise<void> {
  const cams = await listCameras();
  // Filter out devices that lack a label — they're un-useful (and usually
  // duplicates of the labeled ones the OS exposes after permission).
  const labelled = cams.filter((c) => c.label && c.deviceId);
  if (labelled.length === 0) {
    setCamPlaceholder(select);
    return;
  }
  const previous = select.value;
  select.innerHTML = "";
  for (const c of labelled) {
    const opt = document.createElement("option");
    opt.value = c.deviceId;
    opt.textContent = c.label;
    select.appendChild(opt);
  }
  // Preserve the user's selection across refreshes if still present.
  if (previous && [...select.options].some((o) => o.value === previous)) {
    select.value = previous;
  }
}

function sizeOverlayToVideo(state: RuntimeState): void {
  // Prefer the live <video> element's dimensions over the cached cameraStream
  // dims, because mobile streams can renegotiate on rotation / camera switch.
  const fallbackW = state.cameraStream?.width ?? 1280;
  const fallbackH = state.cameraStream?.height ?? 720;
  const w = state.videoEl.videoWidth || fallbackW;
  const h = state.videoEl.videoHeight || fallbackH;
  state.overlay.resizeTo(w, h);
  // Polygon coords are normalized, but the editor needs the current pixel
  // dimensions to translate clicks back into 0-1 space.
  state.polygonEditor.setFrameSize({ w, h });
}

function onConfigChange(state: RuntimeState, cfg: AppConfig, reason: string): void {
  state.config = cfg;
  // Hot-apply where possible
  state.detector.confidenceThreshold = cfg.confidence_threshold;
  state.detector.setClasses(cfg.classes);
  state.tracker.highThresh = cfg.confidence_threshold;
  state.registry.graceSeconds = cfg.track_grace_seconds;

  if (cfg.danger_zone_enabled && cfg.danger_zone_polygon.length >= 3) {
    state.dangerZone = new DangerZone(cfg.danger_zone_polygon);
  } else {
    state.dangerZone = null;
  }

  if (state.dangerZone && cfg.alert_on_danger_zone) {
    if (!state.alerts) {
      state.alerts = new AlertEngine({
        dangerZone: state.dangerZone,
        minPersistenceSeconds: cfg.min_persistence_seconds,
        minConfidence: cfg.min_alert_confidence,
        cooldownSeconds: cfg.alert_cooldown_seconds,
      });
    } else {
      state.alerts.dangerZone = state.dangerZone;
      state.alerts.minPersistenceSeconds = cfg.min_persistence_seconds;
      state.alerts.minConfidence = cfg.min_alert_confidence;
      state.alerts.cooldownSeconds = cfg.alert_cooldown_seconds;
    }
  } else {
    state.alerts = null;
  }

  state.store.setBackend(cfg.storage_mode);
  state.loop?.setFps(cfg.capture_fps);

  if (reason === "webhook") {
    persistWebhook(cfg);
  }

  if (reason === "device") {
    // Device change requires recreating the ORT session.
    void switchInferenceDevice(state, cfg.inference_device);
  }
}

async function switchInferenceDevice(
  state: RuntimeState,
  device: AppConfig["inference_device"],
): Promise<void> {
  const status = document.getElementById("status") as HTMLDivElement;
  const wasRunning = state.loop !== null;
  state.loop?.stop();
  status.textContent = `switching inference target to ${device}…`;
  state.detector = new Detector({
    modelUrl: "/models/yolo11n.onnx",
    device,
    confidenceThreshold: state.config.confidence_threshold,
    classes: state.config.classes,
  });
  await state.detector.load();
  status.textContent = `inference target: ${device}`;
  if (wasRunning) state.loop?.start();
}

async function processFrame(state: RuntimeState): Promise<void> {
  const video = state.videoEl;
  if (video.readyState < 2 || video.videoWidth === 0) return;

  const frameSize = { w: video.videoWidth, h: video.videoHeight };
  // Detect
  const t0 = performance.now();
  const { detections: rawDets, stats } = await state.detector.detect(
    video,
    frameSize.w,
    frameSize.h,
  );
  // Track. tracker.update returns TrackedDetection[] (Detection plus a
  // non-null track_id), which is structurally compatible with Detection[];
  // pass directly to the registry to avoid an extra spread per frame.
  const tracked = state.tracker.update(rawDets);
  const enriched = state.registry.update(tracked);

  // Compute "is anything currently in the zone that would be alert-eligible"
  // — drives the live banner so it disappears as soon as the object leaves.
  const inZoneAlertWorthy = findAlertWorthyInZone(state, enriched, frameSize);
  state.latestDetectionInZone = inZoneAlertWorthy;

  // Alerts (event emission is still cooldown-gated). Persisting the snapshot
  // + IDB write is fire-and-forget so the frame loop never blocks on the
  // JPEG encoder or storage thread — both can be ~50–200 ms on phones.
  if (state.alerts) {
    const newEvents = state.alerts.evaluate(enriched, frameSize);
    for (const ev of newEvents) {
      state.alertCount += 1;
      state.latestEvent = ev;
      void persistAlert(state, ev, video);
    }
  }
  state.inferenceMs = stats.inferenceMs;
  state.detectionCount = enriched.length;

  // FPS measurement
  const now = performance.now();
  if (state.lastFrameTime > 0) {
    const dt = (now - state.lastFrameTime) / 1000;
    if (dt > 0) {
      const inst = 1 / dt;
      state.measuredFps =
        state.measuredFps === 0 ? inst : 0.85 * state.measuredFps + 0.15 * inst;
    }
  }
  state.lastFrameTime = now;
  void t0;

  const alertActiveNow = inZoneAlertWorthy !== null;
  // Build banner text from the *current* detection so seconds keep counting up.
  const banner = alertActiveNow && inZoneAlertWorthy
    ? `Warning: ${inZoneAlertWorthy.cls_name} detected in forward zone ` +
      `for ${(inZoneAlertWorthy.seen_for_seconds ?? 0).toFixed(1)}s. Please verify visually.`
    : undefined;

  // Render
  const polyPx = state.dangerZone
    ? state.dangerZone.pixelPolygon(frameSize)
    : state.polygonEditor.polygon.map((p) => ({
        x: p.x * frameSize.w,
        y: p.y * frameSize.h,
      }));
  state.overlay.draw({
    detections: enriched,
    danger:
      state.config.danger_zone_enabled && polyPx.length > 0
        ? { polygon: polyPx, alertActive: alertActiveNow }
        : undefined,
    alertBanner: banner,
    zoneHint: zoneHint(state),
    hud: {
      fps: state.measuredFps,
      detectionCount: enriched.length,
      trackCount: state.registry.activeCount,
      longestSeconds: state.registry.longestSeconds,
      alertCount: state.alertCount,
      inferenceMs: state.inferenceMs,
      showHud: state.config.show_hud,
      showTrackIds: state.config.show_track_ids,
      showConfidence: state.config.show_confidence,
    },
  });

  // Update small-strip HUD. Storage counter only updates when an event is
  // actually persisted (inside persistAlert) — no per-frame IDB hit.
  updateHudStrip(state);
}

async function persistAlert(
  state: RuntimeState,
  ev: AlertEvent,
  video: HTMLVideoElement,
): Promise<void> {
  try {
    const snapshot = await captureSnapshot(video);
    const enrichedEv: AlertEvent = snapshot
      ? { ...ev, snapshot_path: `snapshots/${ev.event_id}.jpg` }
      : ev;
    await state.store.addEvent(enrichedEv, snapshot ?? undefined);
    await state.events.refresh();
    await updateEventsCounter(state);
    // Outbound webhook — fire-and-forget so a slow/down endpoint never
    // delays the IDB write or the next frame. Empty URL/key fall back to
    // the Sealog demo endpoint inside sendEventWebhook itself.
    if (state.config.webhook_enabled) {
      void sendEventWebhook(
        {
          enabled: true,
          url: state.config.webhook_url,
          apiKey: state.config.webhook_api_key,
        },
        enrichedEv,
        snapshot ?? undefined,
      );
    }
  } catch (err) {
    console.error("persistAlert failed", err);
  }
}

function findAlertWorthyInZone(
  state: RuntimeState,
  detections: Detection[],
  frameSize: { w: number; h: number },
): Detection | null {
  if (!state.dangerZone) return null;
  // Pick the longest-persisted detection that is in zone and meets the
  // alert thresholds — that's the most useful one to surface in the banner.
  let best: Detection | null = null;
  for (const d of detections) {
    if (d.track_id === null) continue;
    if (d.confidence < state.config.min_alert_confidence) continue;
    if (d.seen_for_seconds === null) continue;
    if (d.seen_for_seconds < state.config.min_persistence_seconds) continue;
    const cx = (d.bbox.x1 + d.bbox.x2) / 2;
    const cy = (d.bbox.y1 + d.bbox.y2) / 2;
    if (!state.dangerZone.contains({ x: cx, y: cy }, frameSize)) continue;
    if (best === null || (d.seen_for_seconds ?? 0) > (best.seen_for_seconds ?? 0)) best = d;
  }
  return best;
}

function zoneHint(state: RuntimeState): string | undefined {
  if (!state.config.danger_zone_enabled) return undefined;
  const n = state.polygonEditor.polygon.length;
  if (n === 0) return "Click on the video to draw a danger zone";
  if (n < 3) return `Click ${3 - n} more point${3 - n === 1 ? "" : "s"} to close the polygon`;
  return undefined;
}

function updateHudStrip(state: RuntimeState): void {
  const set = (sel: string, txt: string) => {
    const el = document.querySelector(`[data-stat="${sel}"]`);
    if (el) el.textContent = txt;
  };
  set("fps", `${state.measuredFps.toFixed(1)} fps`);
  set("det", `${state.detectionCount} det · inf ${state.inferenceMs.toFixed(0)}ms`);
  set(
    "tracks",
    `${state.registry.activeCount} tracks · longest ${state.registry.longestSeconds.toFixed(1)}s`,
  );
  set("alerts", `${state.alertCount} alerts`);
  set(
    "latest",
    state.latestDetectionInZone
      ? `${state.latestDetectionInZone.cls_name} in zone ` +
        `(${(state.latestDetectionInZone.seen_for_seconds ?? 0).toFixed(1)}s)`
      : state.latestEvent
        ? AlertEngine.formatBanner(state.latestEvent)
        : "",
  );
}

async function updateEventsCounter(state: RuntimeState): Promise<void> {
  const el = document.getElementById("events-count");
  if (!el) return;
  const n = await state.store.count();
  el.textContent = `${n} / ${MAX_EVENTS}`;
}

async function captureSnapshot(video: HTMLVideoElement): Promise<Blob | null> {
  if (video.videoWidth === 0) return null;
  // Cap snapshot resolution — full-frame JPEG encode is expensive on mobile
  // (50-200 ms). 640px wide preserves enough detail for after-the-fact review
  // without making the encode block the frame loop's heels.
  const MAX_W = 640;
  const scale = Math.min(1, MAX_W / video.videoWidth);
  const w = Math.round(video.videoWidth * scale);
  const h = Math.round(video.videoHeight * scale);
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
}

const WEBHOOK_LS_KEY = "marine-detect:webhook";

function loadPersistedWebhook(): Partial<AppConfig> {
  try {
    const raw = localStorage.getItem(WEBHOOK_LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as {
      enabled?: unknown;
      url?: unknown;
      apiKey?: unknown;
    };
    return {
      webhook_enabled: typeof parsed.enabled === "boolean" ? parsed.enabled : undefined,
      webhook_url: typeof parsed.url === "string" ? parsed.url : undefined,
      webhook_api_key: typeof parsed.apiKey === "string" ? parsed.apiKey : undefined,
    } as Partial<AppConfig>;
  } catch {
    return {};
  }
}

function persistWebhook(cfg: AppConfig): void {
  try {
    localStorage.setItem(
      WEBHOOK_LS_KEY,
      JSON.stringify({
        enabled: cfg.webhook_enabled,
        url: cfg.webhook_url,
        apiKey: cfg.webhook_api_key,
      }),
    );
  } catch {
    // localStorage can be unavailable (private mode quota, etc.) — non-fatal.
  }
}

// Suppress unused-import lint if some helpers remain unused after a refactor.
void COCO_CLASSES;

boot().catch((err) => {
  console.error("boot failed", err);
  const status = document.getElementById("status");
  if (status) status.textContent = `boot error: ${(err as Error).message}`;
});
